import { ArrowRight, Home } from 'lucide-react'
import { useState } from 'react'
import { redirect, useSubmit } from 'react-router'
import { GoogleAddressSearch } from '#app/components/address/GoogleAddressSearch'
import { SimpleAddressSearch } from '#app/components/address/SimpleAddressSearch'
import { Button } from '#app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '#app/components/ui/card'
import { authSessionStorage } from '#app/utils/session.server'
import { type Route } from './+types/start'

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData()
  const addressData = {
    street: formData.get('street') as string,
    houseNumber: formData.get('houseNumber') as string,
    postalCode: formData.get('postalCode') as string,
    city: formData.get('city') as string,
    propertyType: formData.get('propertyType') as string,
    formatted: formData.get('formatted') as string,
    lat: formData.get('lat') as string,
    lng: formData.get('lng') as string,
    streetViewUrl: formData.get('streetViewUrl') as string,
  }

  // Store address data in session
  const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
  authSession.set('addressData', addressData)
  
  return redirect('/questionnaire', {
    headers: {
      'set-cookie': await authSessionStorage.commitSession(authSession),
    },
  })
}

export async function loader(_: Route.LoaderArgs) {
  // Pass Google Maps API key to client
  const apiKey = process.env.GOOGLE_MAPS_API_KEY || ''
  const hasGoogleMaps = !!apiKey && apiKey !== 'your-google-maps-api-key'
  return { apiKey, hasGoogleMaps }
}

export default function StartRoute({ loaderData }: Route.ComponentProps) {
  const submit = useSubmit()
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>('house')
  const [addressData, setAddressData] = useState<any>(null)
  
  // A precise address (street + house number + city) is required so we can
  // map the property to municipality data, subsidies, and Street View.
  const canContinue = !!(addressData?.street && addressData?.houseNumber && addressData?.city)

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement', icon: '🏢' },
    { value: 'house', label: 'Huis', icon: '🏠' },
    { value: 'townhouse', label: 'Rijtjeshuis', icon: '🏘️' },
  ]

  const handleAddressSelect = (address: any) => {
    setAddressData(address)
  }

  const handleSubmit = () => {
    if (!addressData) return
    
    const formData = new FormData()
    formData.set('propertyType', selectedPropertyType)
    formData.set('street', addressData.street || '')
    formData.set('houseNumber', addressData.houseNumber || '')
    formData.set('postalCode', addressData.postalCode || '')
    formData.set('city', addressData.city || '')
    formData.set('formatted', addressData.formatted || '')
    formData.set('lat', addressData.lat?.toString() || '')
    formData.set('lng', addressData.lng?.toString() || '')
    
    // Generate street view URL for storage
    if (addressData.lat && addressData.lng) {
      const streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?size=640x480&location=${addressData.lat},${addressData.lng}&fov=80&pitch=0&key=${loaderData.apiKey}`
      formData.set('streetViewUrl', streetViewUrl)
    }
    
    void submit(formData, { method: 'post' })
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-lg">
              <Home className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welkom bij Woonplan
          </h1>
          <p className="text-lg text-gray-600">
            Laten we je droomverbouwing samen plannen
          </p>
        </div>

        {/* Main Card */}
        <Card className="shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="w-10 h-10 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Wat is je adres?</CardTitle>
            <CardDescription className="text-base">
              We gebruiken dit om lokale regelgeving en de beste aannemers voor je te vinden
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Property Type Selection - Simplified */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Type woning</p>
              <div className="grid grid-cols-3 gap-3">
                {propertyTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSelectedPropertyType(type.value)}
                    className={`relative flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedPropertyType === type.value
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-3xl mb-2">{type.icon}</span>
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Address Search - Google or Simple */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                {loaderData.hasGoogleMaps ? 'Zoek je adres' : 'Vul je adres in'}
              </p>
              {loaderData.hasGoogleMaps ? (
                <GoogleAddressSearch
                  apiKey={loaderData.apiKey}
                  onAddressSelect={handleAddressSelect}
                />
              ) : (
                <SimpleAddressSearch
                  onAddressSelect={handleAddressSelect}
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                size="lg"
                onClick={() => window.location.href = '/'}
              >
                Terug
              </Button>
              <Button 
                onClick={handleSubmit}
                size="lg"
                disabled={!canContinue}
                className="min-w-[150px]"
              >
                Volgende
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trust Badge */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            🔒 Je gegevens zijn veilig en worden nooit zonder toestemming gedeeld
          </p>
        </div>
      </div>
    </div>
  )
}